/** Minimal billing shape — keep local to avoid importing billingUtils (it imports api). */
import {
  isExemptProductName,
  isKnownExemptStripeProductId,
} from '@navigation/screens/MidLevel/UpgradeScreen/utils/exemptProductIds';

type BillingLike = {
  isActive?: boolean;
  status?: string;
  product?: { name?: string; id?: string };
  productName?: string;
  productId?: string;
  product_id?: string;
  metadata?: { productId?: string; [key: string]: unknown };
  [key: string]: unknown;
};

function planIsActive(plan: BillingLike | null | undefined): boolean {
  if (!plan) return false;
  return (
    plan.isActive !== false &&
    plan.status !== 'cancelled' &&
    plan.status !== 'expired' &&
    plan.status !== 'inactive'
  );
}

function planNameIsFree(planName: string | null | undefined): boolean {
  if (!planName) return true;
  if (isKnownExemptStripeProductId(planName)) return false;
  if (isExemptProductName(planName)) return false;
  const lower = planName.toLowerCase();
  return (
    lower.includes('free') ||
    lower.includes('starter')
  );
}

/**
 * Normalizes getSubscriptions / React Query shapes to a single billing-like object.
 */
export function normalizeSubscriptionPlanPayload(plan: unknown): BillingLike | null {
  if (!plan || typeof plan !== 'object') return null;

  if (Array.isArray(plan)) {
    const active = plan.find((item) => planIsActive(item as BillingLike));
    return (active as BillingLike) || null;
  }

  const p = plan as Record<string, unknown>;
  const inner = (p.data as Record<string, unknown>) ?? p;
  
  if (inner && Array.isArray(inner.data)) {
    const active = inner.data.find((item) => planIsActive(item as BillingLike));
    return (active as BillingLike) || (inner.data[0] as BillingLike) || null;
  }

  if (inner && typeof inner === 'object' && inner.data && typeof inner.data === 'object') {
    return inner.data as BillingLike;
  }
  return inner as BillingLike;
}

/**
 * True when cached subscription API data represents an active, non-free paid plan.
 */
export function subscriptionPayloadIndicatesPaid(plan: unknown): boolean {
  const normalized = normalizeSubscriptionPlanPayload(plan);
  if (!normalized) return false;
  if (!planIsActive(normalized)) return false;
  const name =
    normalized?.product?.name ??
    normalized?.productName ??
    normalized?.productId ??
    normalized?.product_id ??
    (normalized?.metadata as Record<string, unknown>)?.productId ??
    '';
  return !planNameIsFree(typeof name === 'string' ? name : String(name));
}

/**
 * Parses Redux userData.billing_info / billing the same way billing hooks expect,
 * and returns true if the user has an active paid (non-free) subscription.
 * Used to suppress false PAYMENT_REQUIRED / billingWarning when API flags limits but account is paid.
 */
export function userDataIndicatesPaidSubscription(userData: unknown): boolean {
  if (!userData || typeof userData !== 'object') return false;
  const u = userData as Record<string, unknown>;

  // Check new entitlements structure
  const entitlements = u.entitlements;
  if (Array.isArray(entitlements)) {
    const active = entitlements.find((e) => planIsActive(e as BillingLike));
    if (active) {
      const activeE = active as BillingLike;
      const name =
        activeE.product?.name ??
        activeE.productName ??
        activeE.productId ??
        activeE.product_id ??
        (activeE.metadata as Record<string, unknown>)?.productId ??
        '';
      if (!planNameIsFree(typeof name === 'string' ? name : String(name))) {
        return true;
      }
    }
  } else if (entitlements && typeof entitlements === 'object') {
    const entObj = entitlements as Record<string, unknown>;
    // Check if it's the { active: ['pro'] } format
    if (entObj.active && Array.isArray(entObj.active)) {
      if (entObj.active.includes('pro') || entObj.active.includes('exempt')) {
        return true;
      }
    } else {
      // It might be a single entitlement object
      const activeE = entitlements as BillingLike;
      if (planIsActive(activeE)) {
        const name =
          activeE.product?.name ??
          activeE.productName ??
          activeE.productId ??
          activeE.product_id ??
          (activeE.metadata as Record<string, unknown>)?.productId ??
          '';
        if (!planNameIsFree(typeof name === 'string' ? name : String(name))) {
          return true;
        }
      }
    }
  }

  // Fallback to legacy billing checks
  const raw = u.billing_info ?? u.billing;
  if (!raw || typeof raw !== 'object') return false;

  let candidates: BillingLike[] = [];
  const r = raw as Record<string, unknown>;
  if (r.isActive !== undefined) {
    candidates = [raw as BillingLike];
  } else {
    candidates = Object.values(raw).filter(Boolean) as BillingLike[];
  }

  const active = candidates.find((s) => s?.isActive || planIsActive(s));
  if (!active) return false;
  if (!planIsActive(active)) return false;

  const name =
    active?.product?.name ??
    active?.productName ??
    active?.productId ??
    active?.product_id ??
    (active?.metadata as Record<string, unknown>)?.productId ??
    '';
  return !planNameIsFree(typeof name === 'string' ? name : String(name));
}
